import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  SearchBox,
  Skeleton,
  SkeletonItem,
  TableCellLayout,
  createTableColumn,
  tokens,
  type DataGridProps,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import { ArrowDownloadRegular } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import { usePdmAssignmentService } from '../../hooks/usePdmAssignmentService';
import { pdmRoleLabel } from '../../constants/pdmRoleLabels';
import { compareString } from '../../utils/comparators';
import { formatPacificDateTimeParts, getPacificTimeZoneAbbr } from '../../utils/formatters';
import { getFriendlyApiErrorMessage, logApiError } from '../../services/apiErrorMessages';
import { exportToCsv } from '../../utils/exportToCsv';
import { trackEvent } from '../../services/telemetry';
import { usePartnerAliases } from '../../contexts/PartnerAliasContext';
import type { PdmAuditRecord } from '../../models/pdmAssignment';
import { PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '../../constants/table';
import { PaginationBar } from '../../components/PaginationBar';

const PACIFIC_ZONE = getPacificTimeZoneAbbr();

const ACTION_LABELS: Record<PdmAuditRecord['action'], string> = {
  assigned: 'Assigned',
  unassigned: 'Unassigned',
};

const formatPersonRole = (name: string | null, role: string | null): string => {
  if (!name) return '';
  return role ? `${name} (${pdmRoleLabel(role)})` : name;
};

const columns: TableColumnDefinition<PdmAuditRecord>[] = [
  createTableColumn<PdmAuditRecord>({
    columnId: 'action',
    compare: (a, b) => compareString(a.action, b.action),
    renderHeaderCell: () => 'Action',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'partner',
    compare: (a, b) => compareString(a.partnerName, b.partnerName),
    renderHeaderCell: () => 'Partner',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'region',
    compare: (a, b) => compareString(a.region, b.region),
    renderHeaderCell: () => 'Region',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'from',
    renderHeaderCell: () => 'From',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'to',
    renderHeaderCell: () => 'To',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'changedBy',
    compare: (a, b) => compareString(a.actorName, b.actorName),
    renderHeaderCell: () => 'Changed by',
    renderCell: () => null,
  }),
  createTableColumn<PdmAuditRecord>({
    columnId: 'when',
    compare: (a, b) => new Date(a.when).getTime() - new Date(b.when).getTime(),
    renderHeaderCell: () => `Date (${PACIFIC_ZONE})`,
    renderCell: () => null,
  }),
];

const COLUMN_IDS = ['action', 'partner', 'region', 'from', 'to', 'changedBy', 'when'] as const;
type HistoryColumnId = (typeof COLUMN_IDS)[number];

const COLUMN_SIZING: Record<HistoryColumnId, { minWidth: number; weight: number }> = {
  action:    { minWidth: 99,  weight: 0.3 },
  partner:   { minWidth: 170, weight: 2.5 },
  region:    { minWidth: 80,  weight: 0.2 },
  from:      { minWidth: 150, weight: 1 },
  to:        { minWidth: 150, weight: 1 },
  changedBy: { minWidth: 180, weight: 1.2 },
  when:      { minWidth: 120, weight: 0.6 },
};

interface HistoryTabProps {
  notify: (message: string, intent: 'success' | 'error') => void;
  active: boolean;
}

export const HistoryTab: React.FC<HistoryTabProps> = ({ active }) => {
  const styles = usePdmAssignmentStyles();
  const { getDisplayName } = usePartnerAliases();
  const service = usePdmAssignmentService();

  const [records, setRecords] = useState<PdmAuditRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Debounce the search box; reset to the first page whenever the query changes.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Fetch the current page from the server (server-side search + paging).
  useEffect(() => {
    if (!service || !active) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    service
      .listAudit({ page, pageSize: PAGE_SIZE, search: searchQuery })
      .then((data) => {
        if (cancelled) return;
        setRecords(data.records);
        setTotal(data.total);
      })
      .catch((err) => {
        if (cancelled) return;
        logApiError('HistoryTab.listAudit', err);
        setError(getFriendlyApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [service, active, page, searchQuery]);

  useEffect(() => {
    trackEvent('pdm_page_view', { tab: 'history' });
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + records.length, total);

  const goToPage = useCallback(
    (target: number) => setPage(Math.max(1, Math.min(target, totalPages))),
    [totalPages],
  );

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages],
  );

  const defaultSortState = useMemo<DataGridProps['defaultSortState']>(
    () => ({ sortColumn: 'when', sortDirection: 'descending' }),
    [],
  );

  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const totalMin = COLUMN_IDS.reduce((s, id) => s + COLUMN_SIZING[id].minWidth, 0);
    const totalWeight = COLUMN_IDS.reduce((s, id) => s + COLUMN_SIZING[id].weight, 0);
    const available = Math.max(containerWidth - 8, totalMin);
    const extra = Math.max(0, available - totalMin);
    const sizing: TableColumnSizingOptions = {};
    COLUMN_IDS.forEach((id) => {
      const { minWidth, weight } = COLUMN_SIZING[id];
      const ideal = totalWeight > 0 ? minWidth + Math.floor((extra * weight) / totalWeight) : minWidth;
      sizing[id] = { minWidth, idealWidth: ideal };
    });
    return sizing;
  }, [containerWidth]);

  const renderCellContent = useCallback(
    (columnId: string, r: PdmAuditRecord) => {
      switch (columnId) {
        case 'action': {
          return (
            <span className={styles.tierCell}>
              {ACTION_LABELS[r.action] ?? r.action}
            </span>
          );
        }
        case 'partner':
          return <span className={styles.partnerLink}>{getDisplayName(r.partnerName)}</span>;
        case 'region':
          return r.region;
        case 'from':
          return formatPersonRole(r.fromPdmName, r.fromRole) || '—';
        case 'to':
          return formatPersonRole(r.toPdmName, r.toRole) || '—';
        case 'changedBy':
          return r.actorName || '—';
        case 'when': {
          const { date, time } = formatPacificDateTimeParts(r.when);
          return (
            <div>
              <div>{date},</div>
              <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{time}</div>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [styles, getDisplayName],
  );

  const handleExport = useCallback(async () => {
    if (!service) return;
    // Export the full filtered set, not just the current page.
    const all: PdmAuditRecord[] = [];
    let p = 1;
    let totalCount = Infinity;
    while (all.length < totalCount) {
      const data = await service.listAudit({ page: p, pageSize: 200, search: searchQuery });
      all.push(...data.records);
      totalCount = data.total;
      if (data.records.length === 0) break;
      p += 1;
    }
    trackEvent('pdm_export', { tab: 'history', rows: all.length });
    exportToCsv<PdmAuditRecord>({
      fileName: 'pdm-assignment-history',
      rows: all,
      columns: [
        { header: 'Action', accessor: (r) => ACTION_LABELS[r.action] ?? r.action },
        { header: 'Partner', accessor: (r) => getDisplayName(r.partnerName) ?? '' },
        { header: 'Region', accessor: (r) => r.region },
        { header: 'From Name', accessor: (r) => r.fromPdmName || '—' },
        { header: 'From Role', accessor: (r) => (r.fromRole ? pdmRoleLabel(r.fromRole) : '—') },
        { header: 'To Name', accessor: (r) => r.toPdmName || '—' },
        { header: 'To Role', accessor: (r) => (r.toRole ? pdmRoleLabel(r.toRole) : '—') },
        { header: 'Changed by', accessor: (r) => r.actorName || '—' },
        {
          header: `Date (${PACIFIC_ZONE})`,
          accessor: (r) => {
            const { date, time } = formatPacificDateTimeParts(r.when);
            return `${date} ${time}`.trim();
          },
        },
      ],
    });
  }, [service, searchQuery, getDisplayName]);

  return (
    <div className={styles.tabContent}>
      {error && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Unable to load history</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div className={styles.exportRow}>
        <Button
          appearance="subtle"
          icon={<ArrowDownloadRegular />}
          onClick={handleExport}
          disabled={total === 0}
        >
          Export to CSV
        </Button>
      </div>

      <div className={styles.filtersRow}>
        <SearchBox
          placeholder="Search by partner, PDM, or actor"
          value={searchInput}
          onChange={(_e, data) => {
            setSearchInput(data.value);
          }}
          style={{ minWidth: '280px' }}
        />
      </div>

      <PaginationBar
        totalItems={total}
        startIndex={startIndex}
        endIndex={endIndex}
        currentPage={page}
        totalPages={totalPages}
        pageOptions={pageOptions}
        onGoToPage={goToPage}
      />

      <div ref={tableWrapperRef} className={styles.tableWrapper}>
        {isLoading && records.length === 0 ? (
          <Skeleton>
            {Array.from({ length: 8 }, (_, row) => (
              <div key={row} style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                {Array.from({ length: 8 }, (_, i) => (
                  <SkeletonItem key={i} style={{ width: '120px', height: '16px' }} />
                ))}
              </div>
            ))}
          </Skeleton>
        ) : total === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery
              ? 'No history entries match the current search.'
              : 'No assignment changes recorded yet. Assign a PDM to start the audit log.'}
          </div>
        ) : (
          <DataGrid
            items={records}
            columns={columns}
            sortable
            resizableColumns
            resizableColumnsOptions={{ autoFitColumns: false }}
            columnSizingOptions={columnSizingOptions}
            defaultSortState={defaultSortState}
            size="medium"
            getRowId={(item) => item.id}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<PdmAuditRecord>>
              {({ item, rowId }) => (
                <DataGridRow<PdmAuditRecord> key={rowId}>
                  {({ columnId }) => (
                    <DataGridCell>
                      <TableCellLayout>{renderCellContent(columnId as string, item)}</TableCellLayout>
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}
      </div>
    </div>
  );
};
